import { useId, useState } from 'react';

interface SubmitLike {
  preventDefault: () => void;
  stopPropagation: () => void;
}

import { useTranslation } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';

import { isAllowedLinkUrl, toVideoEmbedUrl } from './rich-text-extensions';

export interface LinkValue {
  href: string;
  newTab: boolean;
}

interface LinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The link under the cursor, if any; the dialog edits it and offers removal. */
  initial: LinkValue | null;
  onSubmit: (value: LinkValue) => void;
  onRemove: () => void;
}

export function LinkDialog({ open, onOpenChange, initial, onSubmit, onRemove }: LinkDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        {open ? (
          <LinkForm
            initial={initial}
            onCancel={() => onOpenChange(false)}
            onSubmit={onSubmit}
            onRemove={onRemove}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function LinkForm({
  initial,
  onCancel,
  onSubmit,
  onRemove,
}: Omit<LinkDialogProps, 'open' | 'onOpenChange'> & { onCancel: () => void }) {
  const { t } = useTranslation('common');
  const id = useId();
  const [href, setHref] = useState(initial?.href ?? '');
  const [newTab, setNewTab] = useState(initial?.newTab ?? false);
  const [error, setError] = useState(false);

  const submit = (event: SubmitLike) => {
    event.preventDefault();
    event.stopPropagation();
    const value = href.trim();
    if (!isAllowedLinkUrl(value)) {
      setError(true);
      return;
    }
    onSubmit({ href: value, newTab });
  };

  return (
    <form onSubmit={submit} noValidate className="grid gap-4">
      <DialogHeader>
        <DialogTitle>{t(initial ? 'richText.link.editTitle' : 'richText.link.title')}</DialogTitle>
        <DialogDescription>{t('richText.link.description')}</DialogDescription>
      </DialogHeader>
      <div className="grid gap-2">
        <Label htmlFor={`${id}-href`}>{t('richText.link.url')}</Label>
        <Input
          id={`${id}-href`}
          value={href}
          autoFocus
          inputMode="url"
          placeholder="https://"
          aria-invalid={error}
          aria-describedby={error ? `${id}-error` : undefined}
          onChange={(event) => {
            setHref(event.target.value);
            setError(false);
          }}
        />
        {error ? (
          <p id={`${id}-error`} className="text-destructive text-sm">
            {t('richText.link.invalid')}
          </p>
        ) : null}
      </div>
      <div className="flex items-center gap-2">
        <Checkbox
          id={`${id}-tab`}
          checked={newTab}
          onCheckedChange={(checked) => setNewTab(checked === true)}
        />
        <Label htmlFor={`${id}-tab`} className="font-normal">
          {t('richText.link.newTab')}
        </Label>
      </div>
      <DialogFooter className="gap-2 sm:justify-between">
        <div>
          {initial ? (
            <Button type="button" variant="ghost" onClick={onRemove}>
              {t('richText.link.remove')}
            </Button>
          ) : null}
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="ghost" onClick={onCancel}>
            {t('actions.cancel')}
          </Button>
          <Button type="submit">{t('richText.apply')}</Button>
        </div>
      </DialogFooter>
    </form>
  );
}

interface VideoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Receives the embed URL, already normalised to an allowed host. */
  onSubmit: (src: string) => void;
}

export function VideoDialog({ open, onOpenChange, onSubmit }: VideoDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        {open ? <VideoForm onCancel={() => onOpenChange(false)} onSubmit={onSubmit} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function VideoForm({
  onCancel,
  onSubmit,
}: {
  onCancel: () => void;
  onSubmit: (src: string) => void;
}) {
  const { t } = useTranslation('common');
  const id = useId();
  const [url, setUrl] = useState('');
  const [error, setError] = useState(false);

  const submit = (event: SubmitLike) => {
    event.preventDefault();
    event.stopPropagation();
    const src = toVideoEmbedUrl(url);
    if (!src) {
      setError(true);
      return;
    }
    onSubmit(src);
  };

  return (
    <form onSubmit={submit} noValidate className="grid gap-4">
      <DialogHeader>
        <DialogTitle>{t('richText.video.title')}</DialogTitle>
        <DialogDescription>{t('richText.video.description')}</DialogDescription>
      </DialogHeader>
      <div className="grid gap-2">
        <Label htmlFor={`${id}-url`}>{t('richText.video.url')}</Label>
        <Input
          id={`${id}-url`}
          value={url}
          autoFocus
          inputMode="url"
          placeholder="https://www.youtube.com/watch?v=…"
          aria-invalid={error}
          aria-describedby={error ? `${id}-error` : undefined}
          onChange={(event) => {
            setUrl(event.target.value);
            setError(false);
          }}
        />
        {error ? (
          <p id={`${id}-error`} className="text-destructive text-sm">
            {t('richText.video.invalid')}
          </p>
        ) : null}
      </div>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onCancel}>
          {t('actions.cancel')}
        </Button>
        <Button type="submit">{t('richText.video.insert')}</Button>
      </DialogFooter>
    </form>
  );
}

interface AltTextDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial: string;
  onSubmit: (alt: string) => void;
}

export function AltTextDialog({ open, onOpenChange, initial, onSubmit }: AltTextDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        {open ? (
          <AltTextForm initial={initial} onCancel={() => onOpenChange(false)} onSubmit={onSubmit} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function AltTextForm({
  initial,
  onCancel,
  onSubmit,
}: {
  initial: string;
  onCancel: () => void;
  onSubmit: (alt: string) => void;
}) {
  const { t } = useTranslation('common');
  const id = useId();
  const [alt, setAlt] = useState(initial);

  return (
    <form
      noValidate
      className="grid gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onSubmit(alt.trim());
      }}
    >
      <DialogHeader>
        <DialogTitle>{t('richText.image.altTitle')}</DialogTitle>
        <DialogDescription>{t('richText.image.altDescription')}</DialogDescription>
      </DialogHeader>
      <div className="grid gap-2">
        <Label htmlFor={`${id}-alt`}>{t('richText.image.alt')}</Label>
        <Input
          id={`${id}-alt`}
          value={alt}
          autoFocus
          maxLength={500}
          onChange={(event) => setAlt(event.target.value)}
        />
      </div>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onCancel}>
          {t('actions.cancel')}
        </Button>
        <Button type="submit">{t('richText.apply')}</Button>
      </DialogFooter>
    </form>
  );
}
